import { useEffect, useState } from 'react';
import { Ban, CheckCircle, Calendar } from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Avatar } from '@/components/ui/Avatar';
import { Spinner } from '@/components/ui/Spinner';
import api from '@/lib/api';
import type { ApiResponse } from '@hirenfire/shared';

interface BannedUser {
  id: string;
  userId: string;
  displayName: string;
  email: string;
  avatar: string | null;
  reason: string;
  bannedAt: string;
  bannedBy: string;
}

export function AdminBansPage() {
  const [bans, setBans] = useState<BannedUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchBans = async () => {
    try {
      const res = await api.get<ApiResponse<BannedUser[]>>('/admin/bans');
      if (res.data.data) setBans(res.data.data);
    } catch {
      // fallback
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBans();
  }, []);

  const handleUnban = async (userId: string) => {
    try {
      await api.post(`/admin/users/${userId}/unban`);
      fetchBans();
    } catch {
      // handle error
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-white">Banned Users</h1>
        <p className="text-gray-400 mt-1">Manage platform bans</p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Spinner size="lg" />
        </div>
      ) : bans.length === 0 ? (
        <Card>
          <CardBody className="py-16 text-center">
            <Ban className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No banned users</p>
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-3">
          {bans.map((ban) => (
            <Card key={ban.id} className="hover:border-dark-500 transition-all">
              <CardBody>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Avatar src={ban.avatar} name={ban.displayName} size="lg" />
                    <div>
                      <p className="font-semibold text-white">{ban.displayName}</p>
                      <p className="text-sm text-gray-400">{ban.email}</p>
                      <div className="mt-2">
                        <Badge variant="error">BANNED</Badge>
                      </div>
                      <p className="text-sm text-gray-500 mt-2">
                        <span className="font-medium text-gray-400">Reason:</span>{' '}
                        {ban.reason}
                      </p>
                      <div className="flex items-center gap-4 mt-1 text-xs text-gray-600">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Banned on {new Date(ban.bannedAt).toLocaleDateString()}
                        </span>
                        <span>by {ban.bannedBy}</span>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleUnban(ban.userId)}
                    leftIcon={<CheckCircle className="w-4 h-4" />}
                  >
                    Unban
                  </Button>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
